import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: user?.first_name || '', last_name: user?.last_name || '', barn_name: user?.barn_name || '', location: user?.location || '', bio: user?.bio || '', discipline: user?.discipline || '' });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/auth/profile', form);
      updateUser(data);
      setEditing(false);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div>
      <TopBar title="My profile" rightAction={
        editing
          ? <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }} onClick={handleSave} disabled={saving}>{saving ? '...' : 'Save'}</button>
          : <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }} onClick={() => setEditing(true)}>Edit</button>
      } />

      <div className="page-content">
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto 12px' }}>
            <div className="avatar avatar-xl" style={{ background: 'var(--cr-primary-light)' }}>
              {user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : <span style={{ fontSize: 32 }}>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>}
            </div>
            <label style={{ position: 'absolute', bottom: 0, right: 0, width: 30, height: 30, borderRadius: '50%', background: 'var(--cr-primary)', color: '#F5EBDD', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, border: '2px solid white' }}>
              {'\ud83d\udcf7'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('avatar', file);
                try {
                  const { data } = await api.put('/auth/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  updateUser(data);
                  toast.success('Photo updated');
                } catch (err) { toast.error(err.response?.data?.message || 'Photo upload failed'); }
                e.target.value = '';
              }} />
            </label>
          </div>
          <h2>{user?.first_name} {user?.last_name}</h2>
          {user?.barn_name && <p style={{ fontSize: 13, marginTop: 4 }}>📍 {user.barn_name}</p>}
        </div>

        {editing ? (
          <div className="card">
            {[['first_name','First name'],['last_name','Last name'],['barn_name','Barn name'],['location','Location'],['discipline','Discipline']].map(([f, label]) => (
              <div key={f} className="form-group">
                <label className="form-label">{label}</label>
                <input className="form-input" value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} />
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea className="form-input" rows={3} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} />
            </div>
          </div>
        ) : (
          <>
            {user?.bio && <div className="card"><p style={{ fontSize: 13, margin: 0 }}>{user.bio}</p></div>}
            <div className="card" style={{ padding: '6px 14px' }}>
              {[['Email', user?.email], ['Discipline', user?.discipline], ['Location', user?.location]].filter(([, v]) => v).map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{val}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="section-label">Manage</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          {[
            { path: '/horses', icon: '🐴', label: 'My horses', sub: 'Profiles & records' },
            { path: '/contacts', icon: '🩺', label: 'Care team', sub: 'Vets & farriers' },
            { path: '/organizations', icon: '🏠', label: 'Barns', sub: 'Barns & businesses' },
            { path: '/calendar', icon: '📅', label: 'Calendar', sub: 'Lessons & events' },
            { path: '/documents', icon: '📁', label: 'Documents', sub: 'Your vault' },
            { path: '/reminders', icon: '🔔', label: 'Reminders', sub: 'To-dos' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)}
              style={{ background: 'white', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '14px 12px', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 24 }}>{item.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{item.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{item.sub}</span>
            </button>
          ))}
        </div>

        <button onClick={() => navigate('/marketplace')}
          style={{ width: '100%', background: 'white', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 24 }}>🛒</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Used tack marketplace</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Buy & sell with the community</div>
          </div>
        </button>

        <button className="btn btn-danger btn-full btn-lg" onClick={handleLogout}>
          Sign out
        </button>

        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', marginTop: 16 }}>Connected Rider · Horse care, community & marketplace</p>
      </div>
    </div>
  );
};

export default ProfilePage;
